import type {
  CreateCustomerUserDto,
  FindAllUsersQueryDto,
  User,
} from '@/entities/auth/user.entity';
import type { CustomerDataDto } from '@/entities/bookings/customer-data.dto';
import type { PaginatedResponse } from '@/entities/pagination/paginated-response';
import { Service } from './http-service';
import type {
  CreatePartnerSubTypeDto,
  PartnerSubType,
  UpdatePartnerSubTypeDto,
  VatIdValidationResult,
} from '@carvia/ros-client-types';

export interface ValidationResult {
  match: 'match' | 'correction' | 'mismatch' | 'extracted';
  value: string | null;
  candidates: string[];
}

export interface ValidationRow {
  field: string;
  validation: ValidationResult;
}

export type DocumentDataExtractionResult = ValidationRow[];

class UsersService extends Service {
  async createCustomerUser(data: CreateCustomerUserDto) {
    const res = await this.httpClient.post<User>(`${this.endpoint}/customer`, data);
    return res.data;
  }

  async createOrReturnId(data: CreateCustomerUserDto) {
    const res = await this.httpClient.post<{ id: string }>(
      `${this.endpoint}/create-or-return-id`,
      data,
    );
    return res.data;
  }

  async getOneByEmailIfExists(email: string) {
    const res = await this.httpClient.get<User | null>(`${this.endpoint}/email-if-exists/${email}`);
    return res.data;
  }

  async getOneIdByEmailIfExists(email: string) {
    const res = await this.httpClient.get<{ id: string } | null>(
      `${this.endpoint}/id-by-email-if-exists/${email}`,
    );
    return res.data;
  }

  async validateVatId(vatId: string, country?: string | null) {
    const res = await this.httpClient.post<VatIdValidationResult>(
      `${this.endpoint}/vat-id/validate`,
      {
        vatId,
        country: country || undefined,
      },
    );
    return res.data;
  }

  async getAllWithQuery(params: FindAllUsersQueryDto) {
    const res = await this.httpClient.get<PaginatedResponse<User>>(`${this.endpoint}`, {
      params,
    });
    return res.data;
  }

  async getPartnerSubTypes() {
    const res = await this.httpClient.get<PartnerSubType[]>(`${this.endpoint}/partner-sub-types`);
    return res.data;
  }

  async getPartnerSubType(id: string) {
    const res = await this.httpClient.get<PartnerSubType>(
      `${this.endpoint}/partner-sub-types/${id}`,
    );
    return res.data;
  }

  async createPartnerSubType(data: CreatePartnerSubTypeDto) {
    const res = await this.httpClient.post<PartnerSubType>(
      `${this.endpoint}/partner-sub-types`,
      data,
    );
    return res.data;
  }

  async updatePartnerSubType(id: string, data: UpdatePartnerSubTypeDto) {
    const res = await this.httpClient.patch<PartnerSubType>(
      `${this.endpoint}/partner-sub-types/${id}`,
      data,
    );
    return res.data;
  }

  async extractDocuments(
    files: File[],
    customerData: CustomerDataDto,
    bookingNumber: string,
    onlyLicenseData: boolean = false,
  ): Promise<DocumentDataExtractionResult> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('documents', file);
    });
    formData.append('customerData', JSON.stringify(customerData));

    const res = await this.httpClient.post<DocumentDataExtractionResult>(
      `${this.endpoint}/extract-documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: { onlyLicenseData, bookingNumber },
        timeout: 120_000,
      },
    );
    return res.data;
  }

  async resendWelcomeMail(userId: string) {
    const res = await this.httpClient.post<string>(`${this.endpoint}/${userId}/mails/welcome`);
    return res.data;
  }

  async deletePermanently(userId: string) {
    await this.httpClient.delete(`${this.endpoint}/${userId}/permanently`);
  }
}

export const usersService = new UsersService('/users');
